<template>
  <v-navigation-drawer
    ref="drawer"
    class="c-drawer"
    v-bind="$attrs"
    bottom
    fixed
    :floating="isMobile"
    touchless
    :value="value"
    :width="drawerWidth"
    v-on="$listeners"
  >
    <div ref="header" class="c-drawer__header">
      <slot name="header"/>
    </div>
    <div ref="body" class="c-drawer__body">
      <slot name="default" />
    </div>
  </v-navigation-drawer>
</template>

<script>
export default {
  name: 'ResizableDrawer',

  model: {
    prop: 'value',
    event: 'update:value'
  },

  props: {
    value: {
      type: Boolean,
      required: false,
      default: false
    },

    width: {
      type: Number,
      required: false,
      default: 300
    }
  },

  data: function () {
    return {
      borderElement: null,
      drawerElement: null,
      drawerWidth: this.width
    }
  },

  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.mobile
    }
  },

  watch: {
    drawerWidth: {
      immediate: true,
      handler(newWidth, _oldWidth) {
        this.$emit('width', newWidth)
      }
    },

    value: {
      immediate: true,
      handler(newVal, _oldVal) {
        if (newVal) {
          // reset drawer width when opened
          this.drawerWidth = this.width
        }
      }
    }
  },

  mounted: function () {
    // drawer details
    this.drawerElement = this.$refs.drawer.$el
    this.drawerDirection = this.drawerElement.classList.contains('v-navigation-drawer--right')
      ? 'right'
      : 'left'

    // border details
    this.borderElement = this.$refs.drawer.$el.querySelector('.v-navigation-drawer__border')
    this.borderWidth = parseInt(window.getComputedStyle(this.borderElement).width, 10)

    this.addAttributes()
    this.addEvents()
  },

  beforeDestroy: function () {
    this.removeEvents()
  },

  methods: {
    addAttributes() {
      this.borderElement.setAttribute('data-title', this.$t('tour.resizableDrawer.title'))
      this.borderElement.setAttribute('data-intro', this.$t('tour.resizableDrawer.intro'))
    },

    addEvents() {
      // look for mousedown events on the border itself
      this.borderElement.addEventListener('mousedown', this.onMouseDown, false)
      // look for mouseup events anywere they occur
      document.addEventListener('mouseup', this.onMouseUp, false)
    },

    removeEvents() {
      this.borderElement.removeEventListener('mousedown', this.onMouseDown, false)
      document.removeEventListener('mouseup', this.onMouseUp, false)
      document.removeEventListener('mousemove', this.onResizeDrawer, false)
    },

    onMouseDown(_e) {
      this.drawerElement.style.transition = 'initial'
      this.drawerElement.classList.add('c-resizing')
      this.$emit('resizing', true)
      document.addEventListener('mousemove', this.onResizeDrawer, false)
    },

    onMouseUp(_e) {
      this.drawerElement.style.transition = null
      this.drawerElement.classList.remove('c-resizing')
      this.$emit('resizing', false)

      // prevent drawer width going below border width
      const width = parseInt(window.getComputedStyle(this.drawerElement).width, 10)
      this.drawerWidth = width < this.borderWidth ? this.borderWidth : width

      // manage events
      document.removeEventListener('mousemove', this.onResizeDrawer, false)
      this.$emit('width', this.drawerWidth)
    },

    onResizeDrawer(e) {
      const width =
        this.drawerDirection === 'right' ? document.body.scrollWidth - e.clientX : e.clientX
      this.drawerWidth = width
      // this.drawerElement.style.width = width + 'px'
    }
  }
}
</script>

<style lang="scss" scoped>
.c-drawer {
  &.c-resizing {
    pointer-events: none;
    user-select: none;
  }

  :deep(.v-navigation-drawer__content) {
    overscroll-behavior: contain;
  }

  /* drawer border */
  &.v-navigation-drawer--open {
    :deep(.v-navigation-drawer__content) {
      margin-left: 0px;
      margin-right: 8px; /* make room for the border */
    }

    :deep(.v-navigation-drawer__border) {
      border-right: 1px solid var(--v-outline-base);
      width: 8px;
      z-index: 2;

      &::before {
        position: absolute;
        content: '';
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: var(--v-accent-base);
        opacity: 0.25;
      }

      &::after {
        position: absolute;
        content: '\02551';
        top: 50%;
        left: 0;
        transform: translateY(-50%);
        width: 100%;
        height: 24px;
        background-color: var(--v-accent-base);
      }

      &:hover {
        cursor: col-resize;
        background-color: var(--v-primary-base);
      }
    }
  }

  /* mobile drawer (no border; from bottom) */
  &.v-navigation-drawer--is-mobile.v-navigation-drawer--open {
    max-height: 75%;

    :deep(.v-navigation-drawer__content) {
      margin-left: 0px;
    }

    :deep(.v-navigation-drawer__border) {
      width: 0 !important;

      &::after {
        content: '';
      }
    }
  }
}

/* slots */
.c-drawer__header {
  background-color: var(--v-background-base);
  position: sticky;
  z-index: 1;
  top: 0;
  width: 100%;
}
.c-drawer__body {
  width: 100%;
}
</style>
